import { UISref } from "@uirouter/react";
import { FunctionComponent } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { PrimaryButton } from "../../../../components/button";
import { Icon } from "../../../../components/icons";
import { ellipsis } from "../../../../components/layout";
import { Table } from "../../../../components/table";
import { TableHeaders } from "../../../../components/table/Head";
import { Pageable } from "../../../../services/pageable";
import { AppStore } from "../../../../store";
import { UserActions } from "../../../../store/users/actions";
import { UsersDTO } from "../../../../store/users/models/dto";
import { colours } from "../../../../styles/colours";
import { UsersRoutes as routes } from "../../routes";
import { NoUsers } from "../base";
import { PresentationalUsersProps } from "./common";

type SingleUser = UsersDTO.Responses.GetSingle.T;

type fieldNames = keyof SingleUser;

const headers: TableHeaders<fieldNames> = [
  {
    name: "firstName",
    title: "First Name",
  },
  {
    name: "lastName",
    title: "Last Name",
  },
  {
    name: "emailAddress",
    title: "Email Address",
  },
  {
    name: "createdDate",
    title: "Add Delegate",
  },
  {
    name: "userReferenceId",
    title: "",
    centered: true,
    hiddenMobile: true,
  },
];

const OverflowField = styled.span`
  ${ellipsis};
  max-width: 15em;
  display: inline-block;
`;

type DelegateUserListProps = {
  onAddDelegate: (userId: string) => void;
  existingDelegates: Array<string>;
};

export const DelegateUsersList: FunctionComponent<
  Partial<PresentationalUsersProps> & DelegateUserListProps
> = ({ usersList, onAddDelegate, existingDelegates }) => {
  const dispatch = useDispatch();

  const pageableState = useSelector<AppStore, Pageable.T<keyof SingleUser>>(
    (store) => store.users.allUsers.pagination
  );

  const onPaginationChange = (
    x: Pageable.T<Exclude<keyof SingleUser, "createdDate">>
  ) => dispatch(UserActions.setPagination(x));

  return (
    <>
      {!usersList || usersList.pageable.totalElements < 1 ? (
        <NoUsers />
      ) : (
        <>
          <Table
            data={usersList.content}
            head={headers}
            renderFields={{
              userReferenceId: ({ userReferenceId }) => (
                <UISref
                  to={routes.names.single}
                  params={{ id: userReferenceId }}
                >
                  <a>
                    <PrimaryButton variant="contained">
                      <Icon
                        type="export"
                        rotateAmount={270}
                        colour1={colours.white}
                      />
                    </PrimaryButton>
                  </a>
                </UISref>
              ),
              firstName: ({ firstName }) => (
                <OverflowField>{firstName}</OverflowField>
              ),
              lastName: ({ lastName }) => (
                <OverflowField>{lastName}</OverflowField>
              ),
              emailAddress: ({ emailAddress }) => (
                <OverflowField>{emailAddress}</OverflowField>
              ),
              createdDate: ({ userReferenceId }) => (
                <PrimaryButton
                  variant="contained"
                  disabled={existingDelegates.includes(userReferenceId)}
                  onClick={() => onAddDelegate(userReferenceId)}
                >
                  <Icon type="addUser" />
                </PrimaryButton>
              ),
            }}
            unsortable={["createdDate"]}
            pagination={usersList.pageable}
            onPaginationChange={onPaginationChange}
            pageableState={Pageable.removeField(pageableState, "createdDate")}
          />
        </>
      )}
    </>
  );
};
